<template>
  <div class="add-track-form big-m-t d-f jc-c p">
    <div class="small-wrap d-f fd-c g">

      <!-- ADD SONG FROM URL -->
      <div class="form-block">
        <div class="d-f jc-sb">
          <p class="form-block__label mute">Add Song From URL</p>
        </div>
        <div v-for="(link, i) in links" :key="i" class="link-row url-row d-f ai-c g-half">
          <div class="input-container m-0 flex-1">
            <label class="label" :for="`atf-link-${i}`">{{ i === 0 ? 'E.g. https://open.spotify.com/track/...' : 'Add link URL...' }}</label>
            <input
              :id="`atf-link-${i}`"
              type="url"
              :placeholder="i === 0 ? 'https://open.spotify.com/track/...' : 'Add another link'"
              v-model="links[i].url"
              @input="onLinkUrlInput(i)"
              @blur="tryFetchMeta(links[i].url)"
            />
          </div>
          <div v-if="links[i].url" class="input-container m-0 link-title-field">
            <label class="label" :for="`atf-link-title-${i}`">Title</label>
            <input :id="`atf-link-title-${i}`" type="text" v-model="links[i].title" placeholder="Title" />
          </div>
          <button v-if="links.length > 1 && links[i].url" class="secondary square" @click.prevent="removeLink(i)">
            <IconBase :w="15" :h="15"><IconCancel /></IconBase>
          </button>
        </div>
        <p v-if="links[0].url" class="text-link ta-c mute tt-up font-small2" @click.prevent="addLink">Add More Links</p>
      </div>

      <!-- ARTWORK + TITLE + GENRE -->
      <div class="artwork-meta-row d-f g">
        <!-- Square artwork zone -->
        <div class="artwork-zone p-rel pointer" :class="{ 'has-artwork': coverPreview }" @click="$refs.coverInput.click()">
          <img v-if="coverPreview" :src="coverPreview" class="artwork-zone__img" alt="" />
          <div v-else class="artwork-zone__empty d-f fd-c ai-c jc-c g-half">
            <IconBase :w="15" :h="15"><IconImg /></IconBase>
            <span class="font-small mute">Artwork</span>
          </div>
          <div class="artwork-zone__overlay d-f ai-c jc-c">
            <IconBase :w="15" :h="15"><IconUpload /></IconBase>
          </div>
          <input ref="coverInput" type="file" accept="image/*" class="d-n" @change="onCoverSelected" />
        </div>

        <!-- Title + Genre stacked -->
        <div class="d-f fd-c g flex-1">
          <div class="form-block">
            <!-- <p class="form-block__label font-small tt-up mute">Title</p> -->
            <div class="input-container m-0">
              <label class="label" for="atf-title">Track title</label>
              <input type="text" id="atf-title" v-model="title" placeholder="Track title" />
            </div>
          </div>
          <div class="form-block">
            <GenreSelect :standalone="false" v-model="genreSelected" />
          </div>
        </div>
      </div>

      <!-- AUDIO FILE -->
      <div class="form-block">
        <!-- <div class="d-f jc-sb ai-c m-b-half">
          <span class="font-small mute">Audio File</span>
          <span class="font-small mute">Optional</span>
        </div> -->
        <AudioUploadZone v-model="audioData" :show-ranking-note="showAudioRankingNote" />
      </div>

      <!-- CREDITED ARTISTS -->
      <div class="form-block">
        <!-- <div class="d-f jc-sb">
          <p class="form-block__label mute">Add collaborators, featured artists, remixers or producers.</p>
          <p class="text-link" @click.prevent="addCreditRow">Add more</p>
        </div> -->
        <div v-for="(row, i) in pendingCredits" :key="i" class="link-row credit-row d-f ai-c g-half">
          <div class="input-container flex-1 m-0">
            <label class="label" :for="`atf-credit-name-${i}`">Add Collaborator</label>
            <input :id="`atf-credit-name-${i}`" type="text" v-model="pendingCredits[i].name" placeholder="Add Collaborator" />
          </div>
          <div v-if="pendingCredits[i].name" class="input-container flex-1 m-0 credit-link-field">
            <label class="label" :for="`atf-credit-link-${i}`">Optional link</label>
            <input :id="`atf-credit-link-${i}`" type="url" v-model="pendingCredits[i].link" placeholder="Optional link" />
          </div>
          <button v-if="pendingCredits.length > 1 && pendingCredits[i].name" class="secondary square" @click.prevent="removeCreditRow(i)">
            <IconBase :w="15" :h="15"><IconCancel /></IconBase>
          </button>
        </div>
        <div v-if="pendingCredits[0].name" class="button-row d-f ai-c m-t-half">
          <p class="text-link ta-c mute tt-up font-small2 w-100 bs-bb" @click.prevent="addCreditRow">Add more Collaborators</p>
        </div>
      </div>

      <div class="b-bottom"></div>

      <!-- UPLOAD BUTTON -->
      <button
        class="w-100 button d-f ai-c jc-c g tt-up"
        :disabled="isUploading"
        @click.prevent="uploadTrack"
      >
        <IconBase :w="15" :h="15"><IconUpload /></IconBase>
        {{ isUploading ? 'Uploading…' : 'Upload Track' }}
      </button>

      <p v-if="uploadError" class="font-small error-text">{{ uploadError }}</p>
    </div>
  </div>
</template>

<script>
import { rtdb, storage } from '@/db.js';
import { mapGetters, mapState } from 'vuex';
import AudioUploadZone from '@/components/mymusic/AudioUploadZone.vue';
import GenreSelect from '@/components/settings/GenreSelect.vue';
import IconBase from '@/assets/icons/IconBase.vue';
import IconUpload from '@/assets/icons/IconUpload.vue';
import IconPlus from '@/assets/icons/IconPlus.vue';
import IconUser from '@/assets/icons/IconUser.vue';
import IconCancel from '@/assets/icons/IconCancel.vue';
import IconImg from '@/assets/icons/IconImg.vue';
import ToggleSwitch from '@/components/misc/ToggleSwitch.vue';

const PLATFORM_KEYS = {
  'youtube.com': 'youtube',
  'youtu.be': 'youtube',
  'soundcloud.com': 'soundcloud',
  'open.spotify.com': 'spotify',
  'bandcamp.com': 'bandcamp',
  'music.apple.com': 'appleMusic',
  'beatport.com': 'beatport',
};

const PLATFORM_LABELS = {
  youtube: 'YouTube',
  soundcloud: 'SoundCloud',
  spotify: 'Spotify',
  bandcamp: 'Bandcamp',
  appleMusic: 'Apple Music',
  beatport: 'Beatport',
};

function detectPlatform(url) {
  if (!url) return null;
  for (const [host, key] of Object.entries(PLATFORM_KEYS)) {
    if (url.includes(host)) return key;
  }
  return null;
}

export default {
  name: 'AddTrackForm',
  components: { GenreSelect, AudioUploadZone, ToggleSwitch, IconBase, IconUpload, IconPlus, IconUser, IconCancel, IconImg },
  data() {
    return {
      links: [{ url: '', title: '' }],
      title: '',
      coverPreview: null,
      coverFile: null,
      coverFromUrl: null,
      genreSelected: [],
      pendingCredits: [{ name: '', link: '' }],
      audioData: null,
      isUploading: false,
      uploadError: '',
      metaFetching: false,
    };
  },
  computed: {
    ...mapGetters(['getUser', 'getUserSettings']),
    ...mapState(['userType']),
    showAudioRankingNote() {
      return this.$route.path === '/my_music' && this.userType === 'artist';
    },
    validLinks() {
      return this.links.filter(l => l.url.trim());
    },
  },
  methods: {
    addLink() {
      this.links.push({ url: '', title: '' });
    },
    onLinkUrlInput(i) {
      const platform = detectPlatform(this.links[i].url);
      const autoLabel = platform ? PLATFORM_LABELS[platform] : null;
      if (autoLabel && !this.links[i]._titleManual) {
        this.$set(this.links[i], 'title', autoLabel);
      }
    },
    removeLink(i) {
      this.links.splice(i, 1);
    },
    async tryFetchMeta(url) {
      if (!url) return;
      let oembedUrl = null;
      if (/youtube\.com|youtu\.be/.test(url)) {
        oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
      } else if (/soundcloud\.com/.test(url)) {
        oembedUrl = `https://soundcloud.com/oembed?url=${encodeURIComponent(url)}&format=json`;
      } else if (/open\.spotify\.com/.test(url)) {
        oembedUrl = `https://open.spotify.com/oembed?url=${encodeURIComponent(url)}`;
      }
      if (!oembedUrl) return;
      this.metaFetching = true;
      try {
        const resp = await fetch(oembedUrl);
        if (!resp.ok) return;
        const data = await resp.json();
        if (data.title && !this.title) this.title = data.title;
        if (data.thumbnail_url && !this.coverPreview) {
          this.coverFromUrl = data.thumbnail_url;
          this.coverPreview = data.thumbnail_url;
        }
      } catch (_) {
        // silent — metadata fetch is best-effort
      } finally {
        this.metaFetching = false;
      }
    },
    onCoverSelected() {
      const file = this.$refs.coverInput?.files?.[0];
      if (!file) return;
      if (file.size > 3 * 1024 * 1024) {
        alert('Cover art must be under 3 MB');
        return;
      }
      this.coverFile = file;
      this.coverFromUrl = null;
      const reader = new FileReader();
      reader.onload = e => { this.coverPreview = e.target.result; };
      reader.readAsDataURL(file);
    },
    addCreditRow() {
      this.pendingCredits.push({ name: '', link: '' });
    },
    removeCreditRow(i) {
      this.pendingCredits.splice(i, 1);
    },
    async uploadTrack() {
      if (!this.title.trim()) {
        this.uploadError = 'Please add a track title.';
        return;
      }
      this.isUploading = true;
      this.uploadError = '';
      try {
        const uid = this.getUser.data.uid;
        const musicRef = rtdb.ref('userMusic').child(uid);

        // Build links object keyed by platform
        const linksObj = {};
        for (const { url, title } of this.validLinks) {
          const platform = detectPlatform(url);
          const key = platform || `link_${Object.keys(linksObj).length}`;
          linksObj[key] = title?.trim() ? { url: url.trim(), title: title.trim() } : url.trim();
        }

        // Build credits object with numeric keys (RTDB-safe)
        const creditsObj = {};
        this.pendingCredits.filter(c => c.name.trim()).forEach((c, i) => { creditsObj[i] = { name: c.name.trim(), link: c.link.trim() }; });

        const songSettings = {
          userUid: uid,
          artist: this.getUserSettings.username,
          title: this.title.trim(),
          genres: Object.assign({ gen0: 'All Genres' }, ...this.genreSelected.map((g, i) => ({ [`gen${i + 1}`]: g }))),
          album: '',
          ...(Object.keys(linksObj).length ? { links: linksObj } : {}),
          ...(Object.keys(creditsObj).length ? { credits: creditsObj } : {}),
        };

        const pushRef = musicRef.push(songSettings);
        const keyRef = pushRef.getKey();

        let coverUrl = this.coverFromUrl || null;
        let audioUrl = null;

        if (this.coverFile) {
          coverUrl = await storage
            .ref(`userMusic/${uid}/${keyRef}/${this.coverFile.name}`)
            .put(this.coverFile)
            .then(s => s.ref.getDownloadURL());
        }

        if (this.audioData) {
          audioUrl = await storage
            .ref(`userMusic/${uid}/${keyRef}/${this.audioData.name}`)
            .put(this.audioData)
            .then(s => s.ref.getDownloadURL());
        }

        await musicRef.child(keyRef).update({
          songUid: keyRef,
          ...(coverUrl ? { cover: coverUrl } : {}),
          ...(audioUrl ? { audio: audioUrl } : {}),
        });

        if (audioUrl) {
          const rankSettings = { ...songSettings, audio: audioUrl, cover: coverUrl, songUid: keyRef };
          await rtdb.ref('rankSongs').child(keyRef).update({
            songUid: keyRef,
            songSettings: rankSettings,
            ratio: 0,
            ownerType: 'artist',
            ownerUid: uid,
          });
        }

        this.$store.dispatch('togglePopMess', 'Track uploaded!');
        this.$emit('uploaded');
        this.resetForm();
      } catch (err) {
        console.error(err);
        this.uploadError = 'Upload failed. Please try again.';
      } finally {
        this.isUploading = false;
      }
    },
    resetForm() {
      this.links = [{ url: '', title: '' }];
      this.title = '';
      this.coverPreview = null;
      this.coverFile = null;
      this.coverFromUrl = null;
      this.genreSelected = [];
      this.pendingCredits = [{ name: '', link: '' }];
      this.audioData = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";

.form-block {
  display: flex;
  flex-direction: column;
  gap: $mg-desktop-sm;
}

.link-row {
  & + & {
    margin-top: $mg-desktop-sm;
  }
}

.link-title-field {
  width: 120px;
  flex-shrink: 0;
}

@media #{$mobile} {
  .link-row {
    flex-wrap: wrap;

    // First input (URL / Name) takes its own full row
    > .input-container:first-child {
      flex: 1 1 100%;
    }
  }

  // Title and Optional link share a row with the X button
  .link-title-field,
  .credit-link-field {
    flex: 1;
    width: auto;
  }

  .url-row {
    padding-bottom: $mg-desktop-sm;
    border-bottom: $border-small solid $line-color;

    &:last-of-type {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  .credit-row {
    padding-bottom: $mg-desktop-sm;
    border-bottom: $border-small solid $line-color;

    &:last-of-type {
      border-bottom: none;
      padding-bottom: 0;
    }
  }
}

.artwork-meta-row {
  @media #{$mobile} {
    flex-direction: column;

    .artwork-zone {
      align-self: auto;
      width: 100%;
      aspect-ratio: 1;
      max-height: 200px;
    }
  }
}

.artwork-zone {
  align-self: stretch;
  aspect-ratio: 1;
  flex-shrink: 0;
  border-radius: $border-radius-input;
  border: $border-small dashed $line-color;
  background-color: rgba(255, 255, 255, 0.04);
  overflow: hidden;
  transition: border-color 0.15s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }

  &__img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__empty {
    width: 100%;
    height: 100%;
    color: rgba(255, 255, 255, 0.3);
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    opacity: 0;
    transition: opacity 0.15s;
  }

  &.has-artwork:hover &__overlay {
    opacity: 1;
  }
}


.error-text {
  color: #ff4d4d;
  text-align: center;
}
</style>
